import React from 'react';
import { Filter, Search, X, XCircle } from 'lucide-react';
import { hasActiveQueueFilters } from '../../utils/queueFilterUtils';

const PRIORITIES = ['Low', 'Medium', 'High', 'Urgent'];

const toggleId = (list, id, checked) => {
  const current = Array.isArray(list) ? list : [];
  return checked ? [...current, id] : current.filter((item) => item !== id);
};

const QueueSearchFilters = ({
  t,
  searchTerm,
  onSearchChange,
  searchPlaceholder,
  showFilters,
  onToggleFilters,
  showToggleButton = false,
  toggleButtonClassName = '',
  filters,
  onFiltersChange,
  onClearFilters,
  peopleKind = 'worker',
  peopleItems = [],
  deviceCategories = [],
  showUnassigned = false,
  layout = 'stack',
  headerLeft = null,
  accent = 'sky'
}) => {
  const peopleLabel =
    peopleKind === 'partner' ? t('jobs.filterByPartner') : t('jobs.filterByWorker');
  const peopleChipLabel = peopleKind === 'partner' ? t('jobs.partner') : t('jobs.worker');
  const hasActive = hasActiveQueueFilters(filters);
  const accentBtn =
    accent === 'rose'
      ? 'bg-rose-500/20 text-rose-100 hover:bg-rose-500/30 border-rose-400/30'
      : accent === 'amber'
        ? 'bg-amber-500/20 text-amber-100 hover:bg-amber-500/30 border-amber-400/30'
        : 'bg-sky-500/20 text-sky-100 hover:bg-sky-500/30 border-sky-400/30';

  const searchBox = (
    <div
      className={`flex items-center gap-1.5 px-2 py-1 bg-gray-900/70 border border-white/10 rounded-md ${
        layout === 'toolbar' ? 'w-32 sm:w-44' : 'flex-1'
      }`}
    >
      <Search className="h-3 w-3 text-gray-400 flex-shrink-0" />
      <input
        type="text"
        placeholder={searchPlaceholder}
        value={searchTerm}
        onChange={(e) => onSearchChange(e.target.value)}
        className="bg-transparent border-none outline-none text-xs text-white placeholder-gray-500 w-full min-w-0"
        autoCapitalize="sentences"
        autoCorrect="off"
        spellCheck={false}
      />
      {searchTerm ? (
        <button
          type="button"
          onClick={() => onSearchChange('')}
          className="text-gray-500 hover:text-white flex-shrink-0"
          title={t('common.close')}
        >
          <X className="h-3 w-3" />
        </button>
      ) : null}
    </div>
  );

  const filterButton = (showToggleButton || layout === 'toolbar') && (
    <button
      type="button"
      onClick={onToggleFilters}
      className={`relative flex items-center gap-1 px-2 py-1 text-xs rounded-md border transition-colors ${
        showFilters || hasActive
          ? `${accentBtn} border`
          : toggleButtonClassName || 'bg-gray-900/70 text-gray-300 hover:text-white hover:bg-white/10 border-white/10'
      }`}
      title={t('jobs.filters')}
    >
      <Filter className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
      <span className="hidden sm:inline">{t('jobs.filters')}</span>
      {hasActive && (
        <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-sky-400" />
      )}
    </button>
  );

  const controls = (
    <div className="flex items-center gap-1.5 flex-shrink-0 ml-auto">
      {searchBox}
      {filterButton}
    </div>
  );

  return (
    <div className={layout === 'toolbar' ? 'w-full min-w-0' : 'mb-2 sm:mb-3'}>
      {layout === 'toolbar' ? (
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2 min-w-0">
          {headerLeft ? <div className="min-w-0 flex-1">{headerLeft}</div> : null}
          {controls}
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row gap-1.5 sm:gap-2 mb-2">
          {showToggleButton && filterButton}
          {searchBox}
        </div>
      )}

      {showFilters && (
        <div className="bg-gray-700 rounded-lg p-3 sm:p-4 mb-2 border border-gray-600">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs sm:text-sm font-semibold text-white">{t('jobs.filterJobs')}</h3>
            <button
              type="button"
              onClick={onClearFilters}
              className="text-xs text-gray-400 hover:text-white flex items-center gap-1"
            >
              <XCircle className="h-3 w-3" />
              {t('jobs.clearFilters')}
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs text-gray-300 mb-2">{peopleLabel}</label>
              <div className="max-h-40 overflow-y-auto bg-gray-800 rounded border border-gray-600 p-2 space-y-1.5">
                {peopleItems.length === 0 && (
                  <p className="text-[10px] text-gray-500 px-1">—</p>
                )}
                {peopleItems.map((item) => {
                  const isSelected = Array.isArray(filters.people) && filters.people.includes(item.id);
                  return (
                    <label key={item.id} className="flex items-center gap-2 text-xs text-white cursor-pointer hover:bg-gray-700 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) =>
                          onFiltersChange({
                            ...filters,
                            people: toggleId(filters.people, item.id, e.target.checked),
                            unassignedOnly: e.target.checked ? false : filters.unassignedOnly
                          })
                        }
                        className="w-3 h-3 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                      />
                      <span>{item.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-300 mb-2">{t('jobs.filterByDeviceCategory')}</label>
              <div className="max-h-40 overflow-y-auto bg-gray-800 rounded border border-gray-600 p-2 space-y-1.5">
                {deviceCategories.map((category) => {
                  const categoryId = String(category.id || category._id);
                  const isSelected =
                    Array.isArray(filters.deviceCategory) && filters.deviceCategory.includes(categoryId);
                  return (
                    <label key={categoryId} className="flex items-center gap-2 text-xs text-white cursor-pointer hover:bg-gray-700 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) =>
                          onFiltersChange({
                            ...filters,
                            deviceCategory: toggleId(filters.deviceCategory, categoryId, e.target.checked)
                          })
                        }
                        className="w-3 h-3 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                      />
                      <span>{category.name}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-300 mb-2">{t('jobs.filterByPriority')}</label>
              <div className="max-h-40 overflow-y-auto bg-gray-800 rounded border border-gray-600 p-2 space-y-1.5">
                {PRIORITIES.map((priority) => {
                  const isSelected = Array.isArray(filters.priority) && filters.priority.includes(priority);
                  return (
                    <label key={priority} className="flex items-center gap-2 text-xs text-white cursor-pointer hover:bg-gray-700 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) =>
                          onFiltersChange({
                            ...filters,
                            priority: toggleId(filters.priority, priority, e.target.checked)
                          })
                        }
                        className="w-3 h-3 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                      />
                      <span>{t(`jobs.priority${priority}`)}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-300 mb-2">{t('jobs.filterByServiceLocation')}</label>
              <div className="bg-gray-800 rounded border border-gray-600 p-2 space-y-1.5">
                {[
                  { value: 'OnSite', label: t('jobs.onSiteAddress') },
                  { value: 'InWorkshop', label: t('jobs.serviceWorkshop') }
                ].map(({ value, label }) => {
                  const isSelected =
                    Array.isArray(filters.serviceLocation) && filters.serviceLocation.includes(value);
                  return (
                    <label key={value} className="flex items-center gap-2 text-xs text-white cursor-pointer hover:bg-gray-700 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) =>
                          onFiltersChange({
                            ...filters,
                            serviceLocation: toggleId(filters.serviceLocation, value, e.target.checked)
                          })
                        }
                        className="w-3 h-3 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                      />
                      <span>{label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-300 mb-2">{t('jobs.additionalFilters')}</label>
              <div className="bg-gray-800 rounded border border-gray-600 p-2 space-y-1.5">
                <label className="flex items-center gap-2 text-xs text-white cursor-pointer hover:bg-gray-700 p-1 rounded">
                  <input
                    type="checkbox"
                    checked={!!filters.warrantyOnly}
                    onChange={(e) => onFiltersChange({ ...filters, warrantyOnly: e.target.checked })}
                    className="w-3 h-3 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                  />
                  <span>{t('jobs.underWarrantyOnly')}</span>
                </label>
                <label className="flex items-center gap-2 text-xs text-white cursor-pointer hover:bg-gray-700 p-1 rounded">
                  <input
                    type="checkbox"
                    checked={!!filters.inServiceOnly}
                    onChange={(e) => onFiltersChange({ ...filters, inServiceOnly: e.target.checked })}
                    className="w-3 h-3 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                  />
                  <span>{t('jobs.inServiceOnly')}</span>
                </label>
                <label className="flex items-center gap-2 text-xs text-white cursor-pointer hover:bg-gray-700 p-1 rounded">
                  <input
                    type="checkbox"
                    checked={!!filters.returnVisitOnly}
                    onChange={(e) => onFiltersChange({ ...filters, returnVisitOnly: e.target.checked })}
                    className="w-3 h-3 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                  />
                  <span>{t('jobs.returnVisitOnly')}</span>
                </label>
                {showUnassigned && (
                  <label className="flex items-center gap-2 text-xs text-white cursor-pointer hover:bg-gray-700 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={!!filters.unassignedOnly}
                      onChange={(e) =>
                        onFiltersChange({
                          ...filters,
                          unassignedOnly: e.target.checked,
                          people: e.target.checked ? [] : filters.people
                        })
                      }
                      className="w-3 h-3 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                    />
                    <span>{t('jobs.unassignedOnly')}</span>
                  </label>
                )}
              </div>
            </div>
          </div>

          {hasActive && (
            <div className="mt-3 pt-3 border-t border-gray-600">
              <div className="flex flex-wrap gap-2">
                {(filters.people || []).map((id) => {
                  const item = peopleItems.find((p) => p.id === id);
                  return (
                    <span key={id} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded">
                      {peopleChipLabel}: {item?.label || id}
                      <button
                        type="button"
                        onClick={() =>
                          onFiltersChange({ ...filters, people: filters.people.filter((itemId) => itemId !== id) })
                        }
                        className="hover:text-gray-200"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  );
                })}
                {(filters.deviceCategory || []).map((categoryId) => {
                  const category = deviceCategories.find(
                    (cat) => String(cat.id || cat._id) === String(categoryId)
                  );
                  return (
                    <span key={categoryId} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded">
                      {t('jobs.category')}: {category?.name || categoryId}
                      <button
                        type="button"
                        onClick={() =>
                          onFiltersChange({
                            ...filters,
                            deviceCategory: filters.deviceCategory.filter((id) => id !== categoryId)
                          })
                        }
                        className="hover:text-gray-200"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  );
                })}
                {(filters.priority || []).map((priority) => (
                  <span key={priority} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded">
                    {t('jobs.priority')}: {t(`jobs.priority${priority}`)}
                    <button
                      type="button"
                      onClick={() =>
                        onFiltersChange({
                          ...filters,
                          priority: filters.priority.filter((p) => p !== priority)
                        })
                      }
                      className="hover:text-gray-200"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                {(filters.serviceLocation || []).map((loc) => (
                  <span key={loc} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded">
                    {t('jobs.serviceLocation')}: {loc === 'InWorkshop' ? t('jobs.serviceWorkshop') : t('jobs.onSiteAddress')}
                    <button
                      type="button"
                      onClick={() =>
                        onFiltersChange({
                          ...filters,
                          serviceLocation: filters.serviceLocation.filter((v) => v !== loc)
                        })
                      }
                      className="hover:text-gray-200"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
                {filters.warrantyOnly && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded">
                    {t('jobs.underWarrantyOnly')}
                    <button type="button" onClick={() => onFiltersChange({ ...filters, warrantyOnly: false })} className="hover:text-gray-200">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                )}
                {filters.inServiceOnly && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded">
                    {t('jobs.inServiceOnly')}
                    <button type="button" onClick={() => onFiltersChange({ ...filters, inServiceOnly: false })} className="hover:text-gray-200">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                )}
                {filters.returnVisitOnly && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded">
                    {t('jobs.returnVisitOnly')}
                    <button type="button" onClick={() => onFiltersChange({ ...filters, returnVisitOnly: false })} className="hover:text-gray-200">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                )}
                {showUnassigned && filters.unassignedOnly && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded">
                    {t('jobs.unassignedOnly')}
                    <button type="button" onClick={() => onFiltersChange({ ...filters, unassignedOnly: false })} className="hover:text-gray-200">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QueueSearchFilters;
